<script lang="ts">
  // Das untere Panel (docs/shell.md).
  //
  // Was hier hineingehoert: laufende Dinge, die man im Blick behaelt,
  // WAEHREND man an etwas anderem arbeitet. Deshalb liegt es unter der
  // Arbeitsflaeche und nicht daneben: was ist los (Meldungen), was laeuft
  // gerade (Laeufe), und das Terminal. Einen Reiter "Ereignisse" gab es
  // bis 15.10.2026 -- er verwies nur auf die Seite (Tim: „Ich schaue hier
  // nie nach"). Die Seite bleibt: System › Ereignisse, Suche, Meldungen.
  //
  // Alles aus Stores, die ohnehin laufen. Die Vollansicht bleibt die
  // jeweilige Seite; hier steht die kurze Fassung mit einem Weg dorthin.

  import Icon from "../Icon.svelte";
  import Kreuz from "../Kreuz.svelte";
  import { t } from "../i18n.svelte";
  import { getAlertsVisible } from "../alerts-store.svelte";
  import { beobachtePanelLaeufe, getPanelLaeufe } from "../runs-summary.svelte";
  import { getTasksSummary } from "../tasks-summary.svelte";
  import { formatRelative } from "../format";
  import {
    panelHoehe,
    panelReiter,
    schaltePanel,
    setzePanelHoehe,
    zeigeReiter,
    type PanelReiter,
  } from "./chrome.svelte";
  import Griff from "./Griff.svelte";
  import PageHost from "./PageHost.svelte";
  import { oeffnePfad } from "./route-sync.svelte";

  const reiter = $derived(panelReiter());
  const hoehe = $derived(panelHoehe());

  const meldungen = $derived(getAlertsVisible());
  // Zwei Sparmassnahmen an derselben Stelle (gemessen 23.08.2026):
  //
  // 1. Geholt wird NUR, solange dieser Reiter offen ist. Vorher hing der
  //    10-s-Poll am blossen Mounten des Panels -- und das Panel steht
  //    praktisch immer offen, also liefen ~9,5 MB pro Minute auch dann,
  //    wenn jemand die Meldungen las. Die Sichtbarkeits-Regel aus
  //    docs/data-loading.md galt hier nur auf dem Papier.
  // 2. Geholt wird ein Ausschnitt (60 + alles Laufende) statt der vollen
  //    24-h-Liste: 1,58 MB fuer 40 sichtbare Zeilen war das Missverhaeltnis.
  $effect(() => {
    if (reiter !== "laeufe") return;
    return beobachtePanelLaeufe();
  });
  const runs = $derived(getPanelLaeufe()?.runs ?? []);
  // Was laeuft, steht oben -- danach das zuletzt Beendete.
  const laeufe = $derived(
    [...runs]
      .sort((a, b) => {
        const al = a.status === "running" ? 0 : 1;
        const bl = b.status === "running" ? 0 : 1;
        if (al !== bl) return al - bl;
        return (b.started_at ?? "").localeCompare(a.started_at ?? "");
      })
      .slice(0, 40),
  );
  // Die Zahl am Reiter kommt aus der Tasks-Summary, nicht aus der Liste:
  // das Badge muss auch dann stimmen, wenn der Reiter zu ist -- und
  // `running_now` kommt WS-getrieben praktisch gratis, waehrend die
  // Liste dafuer megabyteweise Laufhistorie holen muesste.
  const laufend = $derived(getTasksSummary()?.running_now ?? 0);

  const reiterListe: { id: PanelReiter; label: string; anzahl: number; warn: boolean }[] =
    $derived([
      {
        id: "meldungen",
        label: t("shell.panel_alerts", "Meldungen"),
        anzahl: meldungen.length,
        warn: meldungen.some((m) => m.severity === "critical" || m.severity === "problem"),
      },
      {
        id: "laeufe",
        label: t("shell.panel_runs", "Läufe"),
        anzahl: laufend,
        warn: false,
      },
      {
        id: "terminal",
        label: t("shell.panel_terminal", "Terminal"),
        anzahl: 0,
        warn: false,
      },
    ]);

  function statusFarbe(status: string): string {
    if (status === "running") return "text-warning";
    if (status === "ok" || status === "success") return "text-success";
    if (status === "cancelled") return "text-fg-subtle";
    return "text-danger";
  }
</script>

<Griff
  achse="y"
  label={t("shell.panel_height", "Höhe des Panels ändern")}
  wert={hoehe}
  min={120}
  max={600}
  onzug={(pos) => setzePanelHoehe(window.innerHeight - pos.y - 24)}
  onschritt={(d) => setzePanelHoehe(hoehe + d)}
/>

<section
  class="panel border-border flex flex-col overflow-hidden rounded-md border"
  style="height: {hoehe}px"
  aria-label={t("shell.panel", "Unteres Panel")}
>
  <div class="flex items-center gap-1 px-2">
    {#each reiterListe as r (r.id)}
      <button
        type="button"
        class="reiter"
        class:aktiv={reiter === r.id}
        aria-selected={reiter === r.id}
        role="tab"
        onclick={() => zeigeReiter(r.id)}
      >
        {r.label}
        {#if r.anzahl > 0}
          <span class="zahl" class:warn={r.warn}>{r.anzahl}</span>
        {/if}
      </button>
    {/each}
    <div class="flex-1"></div>
    <button
      type="button"
      class="knopf"
      title={t("shell.panel_close", "Panel schließen")}
      aria-label={t("shell.panel_close", "Panel schließen")}
      onclick={() => schaltePanel(false)}
    >
      <Kreuz />
    </button>
  </div>

  <div class="panel-inhalt @container/seite min-h-0 flex-1 overflow-y-auto px-2 pb-2">
    {#if reiter === "meldungen"}
      {#each meldungen as m (m.id)}
        <!-- Klick fuehrt in den Meldungs-Tab, nicht auf die Quell-Seite
             (Tim, 29.08.): dort steht die Meldung ganz, mit Detail,
             Quittieren und Verlauf. ?open= klappt genau diese auf --
             oeffnePfad traegt die Query mit (route-sync). -->
        <button type="button" class="zeile"
          onclick={() => oeffnePfad(`/system/meldungen?open=${encodeURIComponent(m.id)}`)}>
          <span class="ico {m.severity === 'critical' || m.severity === 'problem' ? 'text-danger' : m.severity === 'warning' ? 'text-warning' : 'text-info'}">
            <Icon name={m.severity === "critical" || m.severity === "problem" ? "alert-octa" : m.severity === "info" ? "info" : "alert-tri"} size={14} />
          </span>
          <span class="min-w-0">
            <span class="text-fg block truncate">{m.title}</span>
            {#if m.detail}<span class="text-fg-subtle block truncate">{m.detail}</span>{/if}
          </span>
          {#if m.ts}<span class="wann">{formatRelative(m.ts)}</span>{/if}
        </button>
      {:else}
        <p class="leer">{t("shell.no_alerts", "Keine offenen Meldungen.")}</p>
      {/each}
    {:else if reiter === "laeufe"}
      {#each laeufe as r (r.id)}
        <button
          type="button"
          class="zeile"
          onclick={() => oeffnePfad("/system/runs?focus=" + encodeURIComponent(r.id))}
        >
          <span class="ico {statusFarbe(r.status)}">
            <Icon name={r.status === "running" ? "loader" : r.status === "ok" ? "check-circle" : "alert-tri"} size={14} class={r.status === "running" ? "motion-safe:animate-spin" : ""} />
          </span>
          <span class="min-w-0">
            <span class="text-fg block truncate">{r.task_name}</span>
            <span class="text-fg-subtle block truncate">
              {r.status}{r.duration_s != null ? ` · ${r.duration_s.toFixed(1)} s` : ""}
            </span>
          </span>
          <span class="wann">{formatRelative(r.started_at)}</span>
        </button>
      {:else}
        <p class="leer">{t("shell.no_runs", "Keine Läufe im gewählten Zeitraum.")}</p>
      {/each}
    {:else if reiter === "terminal"}
      <!-- Kein zweites Terminal: die Seite IST die Komponente, und der
           Wirt kann sie genauso rendern wie in einem Tab. Damit gibt es
           weiterhin genau eine Terminal-Implementierung. -->
      <PageHost path="/system/terminal" />
    {/if}
  </div>
</section>


<style>
  .panel {
    flex: none;
    /* Chrome, nicht Inhalt: der Kasten liegt zwischen Grund und Editor,
       im Hellen dicht am Editor, im Dunkeln dicht am Grund -- wie in
       VS Code. Ton in tokens-light.css/tokens-dark.css. */
    background: var(--shell-kasten, var(--bg));
  }
  /* Wie .inhalt in der Editorfläche: hier IST der Scroll-Container, über
     ihm schwebt nichts. Ohne den Wert galt die Telefon-Vorgabe (3.5rem),
     und die Titelzeile der Terminal-Seite parkte mitten in der Ausgabe
     (Tim 06.10.2026). */
  .panel-inhalt {
    --tabellen-top: 0px;
  }
  .reiter {
    padding: 0.4rem 0.55rem;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--fg-subtle);
    border-bottom: 2px solid transparent;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .reiter:hover {
    color: var(--fg-muted);
  }
  .reiter.aktiv {
    color: var(--fg);
    border-bottom-color: var(--accent);
  }
  .zahl {
    background: var(--bg-elev);
    border-radius: 0.5rem;
    padding: 0 0.3rem;
    font-size: 0.62rem;
    letter-spacing: 0;
    color: var(--fg-muted);
    font-variant-numeric: tabular-nums;
  }
  .zahl.warn {
    background: var(--danger);
    color: #fff;
  }
  .knopf {
    display: grid;
    place-items: center;
    padding: 0.3rem;
    border-radius: 4px;
    color: var(--fg-subtle);
  }
  .knopf:hover {
    background: var(--bg-elev);
    color: var(--fg);
  }
  .zeile {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    width: 100%;
    padding: 0.3rem 0.4rem;
    border-radius: 5px;
    text-align: left;
    font-size: 0.78rem;
  }
  .zeile:hover {
    background: var(--bg-elev);
  }
  .ico {
    flex: none;
    margin-top: 0.1rem;
  }
  .wann {
    margin-left: auto;
    padding-left: 0.5rem;
    color: var(--fg-subtle);
    font-size: 0.7rem;
    white-space: nowrap;
  }
  .leer {
    color: var(--fg-subtle);
    font-size: 0.78rem;
    padding: 0.6rem 0.4rem;
  }
</style>
